/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import type { CustomerNote } from '../screens/hooks/useCustomerServiceRecords';

type Props = {
  notes: CustomerNote[];
  loading: boolean;
  canEdit: boolean;
  notesPage?: number;
  notesHasMore?: boolean;
  onLoadNotesPage?: (page: number) => void;
  onAddNote: (body: string, isAlert: boolean) => Promise<void>;
  onArchiveNote: (noteId: string) => void;
  mobile?: boolean;
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function CustomerNotesSection({
  notes,
  loading,
  canEdit,
  notesPage = 0,
  notesHasMore = false,
  onLoadNotesPage,
  onAddNote,
  onArchiveNote,
  mobile = false,
}: Props) {
  const [showForm, setShowForm] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [isAlert, setIsAlert] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!noteBody.trim() || saving) return;
    setSaving(true);
    try {
      await onAddNote(noteBody.trim(), isAlert);
      setNoteBody('');
      setIsAlert(false);
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PaperSheet
      style={mobile ? styles.panelMobile : styles.panelWide}
      testID="customers.detail.customer-notes"
    >
      <View style={styles.headerRow}>
        <Ionicons name="chatbubble-outline" size={18} color={ticketColors.ink} />
        <Text style={styles.title}>Notas del equipo</Text>
        {canEdit && !showForm ? (
          <TouchableOpacity
            style={styles.addBtn}
            onPress={() => setShowForm(true)}
            accessibilityRole="button"
            accessibilityLabel="Agregar nota"
            testID="customers.detail.notes.add"
          >
            <Ionicons name="add" size={16} color={ticketColors.goldInk} />
            <Text style={styles.addBtnText}>Agregar</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {showForm && canEdit ? (
        <View style={styles.form} testID="customers.detail.notes.form">
          <TextInput
            style={styles.input}
            value={noteBody}
            onChangeText={setNoteBody}
            placeholder="Escribí una nota sobre el cliente…"
            placeholderTextColor={ticketColors.ink3}
            multiline
            numberOfLines={3}
            testID="customers.detail.notes.input"
          />
          <View style={styles.formActions}>
            <TouchableOpacity
              style={styles.alertToggle}
              onPress={() => setIsAlert((v) => !v)}
              accessibilityRole="button"
              accessibilityLabel="Marcar como alerta"
              testID="customers.detail.notes.alert-toggle"
            >
              <Ionicons
                name={isAlert ? 'warning' : 'warning-outline'}
                size={16}
                color={isAlert ? ticketColors.bad : ticketColors.ink3}
              />
              <Text style={[styles.alertToggleText, isAlert && styles.alertToggleActive]}>
                Alerta
              </Text>
            </TouchableOpacity>
            <View style={styles.formBtns}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => {
                  setShowForm(false);
                  setNoteBody('');
                  setIsAlert(false);
                }}
                disabled={saving}
              >
                <Text style={styles.cancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveBtn, (!noteBody.trim() || saving) && styles.saveBtnDisabled]}
                onPress={handleSave}
                disabled={!noteBody.trim() || saving}
                testID="customers.detail.notes.save"
              >
                <Text style={styles.saveText}>{saving ? 'Guardando…' : 'Guardar'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      ) : null}

      {loading ? (
        <Text style={styles.emptyText}>Cargando notas…</Text>
      ) : notes.length === 0 && !showForm ? (
        <Text style={styles.emptyText}>Sin notas.</Text>
      ) : (
        <View style={styles.list}>
          {notes.map((note) => (
            <View key={note.id} style={styles.noteCard} testID="customers.detail.note">
              {note.isAlert ? (
                <View style={styles.alertBadge}>
                  <Ionicons name="warning" size={12} color={ticketColors.bad} />
                  <Text style={styles.alertBadgeText}>Alerta</Text>
                </View>
              ) : null}
              <Text style={styles.noteBody}>{note.body}</Text>
              <View style={styles.noteFooter}>
                <Text style={styles.noteDate}>{formatDate(note.createdAt)}</Text>
                {canEdit ? (
                  <TouchableOpacity
                    onPress={() => { onArchiveNote(note.id); }}
                    accessibilityRole="button"
                    accessibilityLabel="Archivar nota"
                    testID={`customers.detail.note.archive.${note.id}`}
                  >
                    <Ionicons name="trash-outline" size={14} color={ticketColors.ink3} />
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
          ))}
        </View>
      )}

      {(notesHasMore || notesPage > 0) && onLoadNotesPage ? (
        <View style={styles.pagerRow} testID="customers.detail.notes-pager">
          <TouchableOpacity
            style={[styles.pagerBtn, notesPage === 0 && styles.pagerBtnDisabled]}
            onPress={() => notesPage > 0 && onLoadNotesPage(notesPage - 1)}
            disabled={notesPage === 0 || loading}
            accessibilityRole="button"
            accessibilityLabel="Página anterior"
          >
            <Ionicons name="chevron-back" size={16} color={notesPage === 0 ? ticketColors.ink3 : ticketColors.ink} />
            <Text style={[styles.pagerText, notesPage === 0 && styles.pagerTextDisabled]}>Anterior</Text>
          </TouchableOpacity>
          <Text style={styles.pagerInfo}>Pág. {notesPage + 1}</Text>
          <TouchableOpacity
            style={[styles.pagerBtn, !notesHasMore && styles.pagerBtnDisabled]}
            onPress={() => notesHasMore && onLoadNotesPage(notesPage + 1)}
            disabled={!notesHasMore || loading}
            accessibilityRole="button"
            accessibilityLabel="Página siguiente"
          >
            <Text style={[styles.pagerText, !notesHasMore && styles.pagerTextDisabled]}>Siguiente</Text>
            <Ionicons name="chevron-forward" size={16} color={!notesHasMore ? ticketColors.ink3 : ticketColors.ink} />
          </TouchableOpacity>
        </View>
      ) : null}
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  panelWide: {
    padding: spacing.md,
  },
  panelMobile: {
    padding: spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink,
    flex: 1,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  addBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.goldInk,
  },
  form: {
    marginBottom: spacing.sm,
  },
  input: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    padding: spacing.sm,
    fontSize: 14,
    color: ticketColors.ink,
    minHeight: 60,
    marginBottom: spacing.xs,
    textAlignVertical: 'top',
  },
  formActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  alertToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  alertToggleText: {
    fontSize: 13,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
  alertToggleActive: {
    color: ticketColors.bad,
    fontWeight: '700',
  },
  formBtns: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  cancelBtn: {
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
  },
  cancelText: {
    fontSize: 13,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
  saveBtn: {
    backgroundColor: ticketColors.gold,
    paddingVertical: 6,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
  saveText: {
    fontSize: 13,
    color: '#fff',
    fontWeight: '700',
  },
  list: {
    gap: spacing.sm,
  },
  noteCard: {
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  alertBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
  },
  alertBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.bad,
  },
  noteBody: {
    fontSize: 14,
    color: ticketColors.ink2,
    marginBottom: 4,
  },
  noteFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noteDate: {
    fontSize: 11,
    color: ticketColors.ink3,
  },
  emptyText: {
    fontSize: 14,
    color: ticketColors.ink3,
    paddingVertical: spacing.sm,
  },
  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    marginTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  pagerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    minHeight: 44,
  },
  pagerBtnDisabled: {
    opacity: 0.4,
  },
  pagerText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  pagerTextDisabled: {
    color: ticketColors.ink3,
  },
  pagerInfo: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
});
